import React from "react";
import { Settings2, MousePointerClick, Plus, X, GripVertical } from "lucide-react";
import SearchableSelect from "modules/swiftboard/components/ui/SearchableSelect";
import RichHtmlEditor from "modules/swiftboard/components/ui/RichHtmlEditor";
import Toggle from "modules/swiftboard/components/ui/Toggle";
import ImageUploadField from "modules/swiftboard/components/ui/ImageUploadField";
import {
  getBlockType,
  getBlockColors,
  NOTE_TYPES,
  CODE_LANGUAGES,
  IMAGE_SIZES,
  ALIGN_OPTIONS,
  GALLERY_COLUMNS,
} from "./blocks.data";

const HEADING_LEVELS = [
  { value: 1, label: "Heading 1" },
  { value: 2, label: "Heading 2" },
  { value: 3, label: "Heading 3" },
  { value: 4, label: "Heading 4" },
];

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[11px] font-semibold uppercase tracking-wide text-text_muted mb-1.5">{label}</label>
    {children}
  </div>
);

const inputClass =
  "w-full px-3 py-2 text-sm rounded-lg outline-none border border-border bg-transparent text-text_primary focus:border-border_strong focus:ring-2 focus:ring-border";

const SegmentedControl = ({ options, value, onChange }) => (
  <div className="inline-flex rounded-lg border border-border p-0.5 bg-surface_alt gap-0.5">
    {options.map((opt) => (
      <button
        key={opt.value}
        type="button"
        onClick={() => onChange(opt.value)}
        className={`px-2.5 py-1.5 text-[11px] font-semibold rounded-md ${
          value === opt.value ? "bg-primary text-white" : "text-text_secondary"
        }`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const ItemListEditor = ({ items, onChange }) => {
  const list = items?.length ? items : [""];

  const updateItem = (i, val) => {
    const next = [...list];
    next[i] = val;
    onChange(next);
  };
  const removeItem = (i) => onChange(list.filter((_, idx) => idx !== i));
  const addItem = () => onChange([...list, ""]);

  return (
    <div className="space-y-2">
      {list.map((item, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <GripVertical size={13} className="text-text_muted flex-shrink-0" />
          <input
            className={`${inputClass} flex-1 py-1.5`}
            value={item}
            placeholder={`Item ${i + 1}`}
            onChange={(e) => updateItem(i, e.target.value)}
          />
          <button
            type="button"
            onClick={() => removeItem(i)}
            disabled={list.length === 1}
            className="p-1 text-danger flex-shrink-0 disabled:opacity-25 disabled:cursor-not-allowed"
          >
            <X size={14} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-primary"
      >
        <Plus size={13} />
        Add item
      </button>
    </div>
  );
};

const GalleryImageEditor = ({ images, onChange }) => {
  const list = images?.length ? images : [{ src: "", alt: "", caption: "" }];

  const updateImage = (i, patch) => {
    const next = [...list];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const removeImage = (i) => onChange(list.filter((_, idx) => idx !== i));
  const addImage = () => onChange([...list, { src: "", alt: "", caption: "" }]);

  return (
    <div className="space-y-2.5">
      {list.map((img, i) => (
        <div key={i} className="p-2.5 rounded-lg border border-border space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-text_muted">Image {i + 1}</span>
            <button
              type="button"
              onClick={() => removeImage(i)}
              disabled={list.length === 1}
              className="p-1 text-danger disabled:opacity-25 disabled:cursor-not-allowed"
            >
              <X size={14} />
            </button>
          </div>
          <ImageUploadField
            variant="compact"
            value={img.src}
            onChange={(src) => updateImage(i, { src })}
            projectTitle="knowledge-article-gallery"
          />
          <input
            className={`${inputClass} py-1.5`}
            placeholder="Caption (optional)"
            value={img.caption || ""}
            onChange={(e) => updateImage(i, { caption: e.target.value })}
          />
        </div>
      ))}
      <button
        type="button"
        onClick={addImage}
        className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-primary"
      >
        <Plus size={13} />
        Add Image
      </button>
    </div>
  );
};

const PropertiesPanel = ({ block, onChange }) => {
  if (!block) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center px-6 py-10">
        <div className="w-12 h-12 rounded-2xl bg-surface_alt flex items-center justify-center mb-3">
          <MousePointerClick size={20} className="text-text_muted" />
        </div>
        <p className="text-[13px] font-medium text-text_primary">Nothing selected</p>
        <p className="text-xs text-text_muted leading-relaxed mt-1">
          Click a content block to edit its properties here.
        </p>
      </div>
    );
  }

  const blockType = getBlockType(block.type);
  const colors = getBlockColors(block.type);
  const update = (patch) => onChange(block.id, { ...block.data, ...patch });

  return (
    <div className="px-4 py-4 space-y-5">
      <div className="flex items-center gap-2.5 pb-3.5 border-b border-border sticky top-0 bg-surface">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors.bg}`}>
          <blockType.icon size={15} className={colors.text} />
        </div>
        <div>
          <p className="text-[13px] font-semibold text-text_primary">{blockType.label}</p>
          <p className="text-[10.5px] text-text_muted flex items-center gap-1">
            <Settings2 size={9} /> Block properties
          </p>
        </div>
      </div>

      {block.type === "heading" && (
        <>
          <Field label="Text">
            <input
              className={inputClass}
              value={block.data.text}
              onChange={(e) => update({ text: e.target.value })}
            />
          </Field>
          <Field label="Heading Level">
            <SearchableSelect
              options={HEADING_LEVELS}
              value={block.data.level}
              onChange={(val) => update({ level: Number(val) })}
            />
          </Field>
          <Field label="Alignment">
            <SegmentedControl options={ALIGN_OPTIONS} value={block.data.align || "left"} onChange={(val) => update({ align: val })} />
          </Field>
        </>
      )}

      {block.type === "paragraph" && (
        <Field label="Text">
          <RichHtmlEditor value={block.data.text} onChange={(html) => update({ text: html })} minHeight={200} />
        </Field>
      )}

      {(block.type === "bulletList" || block.type === "numberedList") && (
        <Field label="Items">
          <ItemListEditor items={block.data.items} onChange={(items) => update({ items })} />
        </Field>
      )}

      {block.type === "image" && (
        <>
          <Field label="Image">
            <ImageUploadField
              value={block.data.src}
              onChange={(src) => update({ src })}
              projectTitle="knowledge-article-content"
              previewClassName="h-32"
              emptyLabel="Upload an image"
            />
          </Field>
          <Field label="Alt Text">
            <input
              className={inputClass}
              value={block.data.alt}
              onChange={(e) => update({ alt: e.target.value })}
            />
          </Field>
          <Field label="Caption">
            <input
              className={inputClass}
              value={block.data.caption}
              onChange={(e) => update({ caption: e.target.value })}
            />
          </Field>
          <Field label="Size">
            <SegmentedControl options={IMAGE_SIZES} value={block.data.size || "medium"} onChange={(val) => update({ size: val })} />
          </Field>
          <Field label="Alignment">
            <SegmentedControl options={ALIGN_OPTIONS} value={block.data.align || "center"} onChange={(val) => update({ align: val })} />
          </Field>
        </>
      )}

      {block.type === "gallery" && (
        <>
          <Field label="Columns">
            <SegmentedControl
              options={GALLERY_COLUMNS}
              value={block.data.columns || 2}
              onChange={(val) => update({ columns: val })}
            />
          </Field>
          <Field label="Images">
            <GalleryImageEditor images={block.data.images} onChange={(images) => update({ images })} />
          </Field>
        </>
      )}

      {block.type === "code" && (
        <>
          <Field label="Language">
            <SearchableSelect
              options={CODE_LANGUAGES.map((lang) => ({ value: lang, label: lang }))}
              value={block.data.language}
              onChange={(val) => update({ language: val })}
            />
          </Field>
          <Field label="Code">
            <textarea
              rows={8}
              className={`${inputClass} resize-none font-mono text-xs`}
              value={block.data.code}
              onChange={(e) => update({ code: e.target.value })}
            />
          </Field>
          <div className="flex items-center justify-between">
            <p className="text-[12px] font-medium text-text_primary">Show line numbers</p>
            <Toggle checked={!!block.data.showLineNumbers} onChange={(v) => update({ showLineNumbers: v })} size="sm" />
          </div>
        </>
      )}

      {block.type === "note" && (
        <>
          <Field label="Note Type">
            <SearchableSelect
              options={NOTE_TYPES}
              value={block.data.noteType}
              onChange={(val) => update({ noteType: val })}
            />
          </Field>
          <Field label="Title (optional)">
            <input
              className={inputClass}
              placeholder="e.g. Heads up"
              value={block.data.title || ""}
              onChange={(e) => update({ title: e.target.value })}
            />
          </Field>
          <Field label="Text">
            <textarea
              rows={4}
              className={`${inputClass} resize-none`}
              value={block.data.text}
              onChange={(e) => update({ text: e.target.value })}
            />
          </Field>
        </>
      )}
    </div>
  );
};

export default PropertiesPanel;
